import { useMemo, useState } from 'react';

import {
  OPERATOR_OPTIONS,
  operatorGlyph,
  renderRuleDescription,
} from '../services/ruleText';
import {
  isSumOperator,
  type CrossFieldRule,
  type HitlActionRequest,
  type HitlActionType,
  type HitlItem,
  type RuleCandidate,
  type RuleOperator,
} from '../services/types';

import { Button } from './Button';

// v0.5 Stage E.2 — default tolerance for sum rules. Mirrors the
// backend's ``core.rule_engine.DEFAULT_SUM_TOLERANCE``.
const DEFAULT_SUM_TOLERANCE = 0.01;

export interface RuleCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

/**
 * Card renderer for v0.4 cross-field rule HITLs.
 *
 * Both CPA rule-confirmation HITLs and validator threshold HITLs carry
 * `resolution_type === "cross_field_rule"`. They share this card
 * family but have **disjoint action sets** — the flavour is decided
 * by `hit.agent_id`:
 *
 *   - ``"cpa"`` → CPA confirmation card.
 *     Pre-validation. Asks the reviewer to confirm a rule the heuristics
 *     proposed: Confirm / Modify operator / Reject.
 *
 *   - ``"cross_field_validator"`` → validator threshold card.
 *     Post-validation. Reports a rule whose violation ratio breached
 *     the 10% threshold and asks the reviewer how to handle it:
 *     Accept violations / Clear failing cells / Tag for review.
 *     The latter two are suppressed for ``not_null_when`` rules where
 *     applicable.
 *
 * Lives outside HitlQueue.tsx so the existing column-datatype /
 * format-decision / table-boundary card paths stay untouched. The
 * routing branch in HitlQueue/HitRow delegates to this component
 * when ``hit.resolution_type === "cross_field_rule"``.
 */
export function RuleCard({ hit, onAction, reviewerId }: RuleCardProps) {
  const candidate = useMemo<RuleCandidate | null>(() => {
    const cands = hit.candidates as RuleCandidate[] | null | undefined;
    if (!cands || cands.length === 0) return null;
    return cands[0];
  }, [hit]);

  if (!candidate || !candidate.rule) {
    // Defensive: a rule HITL without a candidate payload can't be
    // rendered usefully. Render a degraded card with the raw issue
    // text so the reviewer still sees something rather than blanking
    // the queue.
    return <DegradedRuleCard hit={hit} />;
  }

  if (hit.agent_id === 'cross_field_validator') {
    return (
      <ValidatorThresholdCard
        hit={hit}
        candidate={candidate}
        onAction={onAction}
        reviewerId={reviewerId}
      />
    );
  }

  // Default: CPA confirmation card. Treats anything that isn't the
  // validator as the confirmation flavour.
  return (
    <CpaConfirmationCard
      hit={hit}
      candidate={candidate}
      onAction={onAction}
      reviewerId={reviewerId}
    />
  );
}

// ---------------------------------------------------------------------------
// CPA confirmation card
// ---------------------------------------------------------------------------

interface InnerCardProps {
  hit: HitlItem;
  candidate: RuleCandidate;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

function CpaConfirmationCard({
  hit,
  candidate,
  onAction,
  reviewerId,
}: InnerCardProps) {
  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [chosenOperator, setChosenOperator] = useState<RuleOperator>(
    candidate.rule.operator,
  );

  // v0.5 Stage E.2 — parts-list + tolerance state for summation rules.
  // Initialised from the candidate; reviewer edits track here so we
  // can compute "what changed" at confirm time.
  const initialSumColumns = useMemo(
    () => candidate.rule.sum_columns ?? [],
    [candidate.rule.sum_columns],
  );
  const initialTolerance = candidate.rule.tolerance ?? DEFAULT_SUM_TOLERANCE;
  const [sumColumns, setSumColumns] = useState<string[]>(initialSumColumns);
  const [tolerance, setTolerance] = useState<number>(initialTolerance);

  const isSumRule = isSumOperator(chosenOperator);
  const wasSumRule = isSumOperator(candidate.rule.operator);

  // Live description: tracks operator + parts-list + tolerance edits
  // so the headline always reflects what the reviewer is about to
  // confirm. Drops ``description`` whenever any field changed so the
  // synthesiser regenerates from the live shape.
  const liveRule: CrossFieldRule = useMemo(() => {
    const operatorChanged = modifyOpen && chosenOperator !== candidate.rule.operator;
    const partsChanged =
      isSumRule && !arraysEqual(sumColumns, initialSumColumns);
    const toleranceChanged = isSumRule && tolerance !== initialTolerance;
    const dirty = operatorChanged || partsChanged || toleranceChanged;
    return {
      ...candidate.rule,
      operator: modifyOpen ? chosenOperator : candidate.rule.operator,
      sum_columns: isSumRule ? sumColumns : candidate.rule.sum_columns,
      tolerance: isSumRule ? tolerance : candidate.rule.tolerance,
      description: dirty ? undefined : candidate.rule.description,
    };
  }, [
    candidate.rule,
    modifyOpen,
    chosenOperator,
    isSumRule,
    sumColumns,
    tolerance,
    initialSumColumns,
    initialTolerance,
  ]);

  const tier = candidate.confidence_tier ?? hit.hitl_tier ?? 'recommended';
  const tierStyle =
    tier === 'recommended'
      ? 'border-l-4 border-l-brand'
      : 'border-l-4 border-l-warn';

  const dispatch = async (action: HitlActionRequest) => {
    setPending(action.type);
    setError(null);
    try {
      await onAction(hit.hit_id, action);
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  // Confirm-button disabling: a sum rule with an empty parts-list is
  // structurally invalid (no sum to compute). Block dispatch with a
  // hint rather than letting the backend reject the modify_rule call.
  const sumRuleInvalid = isSumRule && sumColumns.length === 0;

  const onConfirmClick = () => {
    if (sumRuleInvalid) return;
    const operatorChanged =
      modifyOpen && chosenOperator !== candidate.rule.operator;
    const partsChanged =
      isSumRule && !arraysEqual(sumColumns, initialSumColumns);
    const toleranceChanged = isSumRule && tolerance !== initialTolerance;

    if (operatorChanged || partsChanged || toleranceChanged) {
      const payload: Record<string, unknown> = {};
      if (operatorChanged) payload.modified_operator = chosenOperator;
      if (partsChanged) payload.modified_sum_columns = sumColumns;
      if (toleranceChanged) payload.modified_tolerance = tolerance;
      return dispatch({
        type: 'modify_rule',
        payload,
        reviewer_id: reviewerId,
      });
    }
    return dispatch({ type: 'confirm_rule', reviewer_id: reviewerId });
  };

  const onRejectClick = () =>
    dispatch({ type: 'reject_rule', reviewer_id: reviewerId });

  const onModifyToggle = () => setModifyOpen((v) => !v);

  // Confirm-button label tracks whether a modify_rule dispatch is
  // pending so the reviewer can see what's about to happen.
  const operatorChanged =
    modifyOpen && chosenOperator !== candidate.rule.operator;
  const partsChanged =
    isSumRule && !arraysEqual(sumColumns, initialSumColumns);
  const toleranceChanged = isSumRule && tolerance !== initialTolerance;
  const willDispatchModify =
    operatorChanged || partsChanged || toleranceChanged;

  return (
    <li
      data-rule-card="cpa-confirmation"
      data-resolution-type="cross_field_rule"
      className={`rounded-md border bg-paper ${tierStyle} border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <CardHeader
        title={renderRuleDescription(liveRule)}
        tierLabel={tier === 'recommended' ? 'recommended' : 'unclear'}
      />
      <RuleSummary rule={liveRule} />
      {/* v0.5 Stage E.2 — parts-list + tolerance editor on summation
          rules. Always visible (no Modify toggle) so the reviewer can
          see and adjust the proposed parts directly. */}
      {isSumRule && (
        <SumRuleEditor
          sumColumns={sumColumns}
          tolerance={tolerance}
          onSumColumnsChange={setSumColumns}
          onToleranceChange={setTolerance}
          disabled={pending !== null || resolved}
        />
      )}
      {modifyOpen && (
        <OperatorPicker
          value={chosenOperator}
          onChange={(op) => {
            setChosenOperator(op);
            // When switching INTO a sum operator from a pairwise one,
            // initialise the parts list from the candidate (if it had
            // one) — otherwise empty. Reviewer must add ≥ 1 part.
            const switchingToSum = isSumOperator(op) && !wasSumRule;
            if (switchingToSum && sumColumns.length === 0) {
              setSumColumns(candidate.rule.sum_columns ?? []);
            }
          }}
        />
      )}
      {sumRuleInvalid && (
        <p
          className="text-xs text-warn"
          data-testid="sum-rule-empty-parts-warning"
        >
          Add at least one part column to confirm this rule.
        </p>
      )}
      {error && <p className="text-sm text-danger">{error}</p>}
      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={onConfirmClick}
            disabled={pending !== null || sumRuleInvalid}
            data-testid="rule-confirm"
          >
            {pending === 'confirm_rule' || pending === 'modify_rule'
              ? 'Confirming…'
              : willDispatchModify
                ? 'Confirm with changes'
                : 'Confirm'}
          </Button>
          <Button
            variant="secondary"
            onClick={onModifyToggle}
            disabled={pending !== null}
            data-testid="rule-modify-toggle"
          >
            {modifyOpen ? 'Cancel modify' : 'Modify operator'}
          </Button>
          <Button
            variant="ghost"
            onClick={onRejectClick}
            disabled={pending !== null}
            data-testid="rule-reject"
          >
            {pending === 'reject_rule' ? 'Rejecting…' : 'Reject'}
          </Button>
        </div>
      )}
    </li>
  );
}

/** Stable shallow comparison for two string arrays in declaration order. */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Validator threshold card
// ---------------------------------------------------------------------------

type ThresholdAction = 'accept' | 'clear_failing_cells' | 'tag_for_review';

function ValidatorThresholdCard({
  hit,
  candidate,
  onAction,
  reviewerId,
}: InnerCardProps) {
  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [chosen, setChosen] = useState<ThresholdAction | null>(null);

  const operator = candidate.rule.operator;
  const isNotNullWhen = operator === 'not_null_when';

  // Available radio options. clear_failing_cells is suppressed for
  // not_null_when rules — the col_a cell is null on violation by
  // definition, so clearing it has no effect. The backend mirrors
  // this filter when emitting the HITL options list, but we re-apply
  // here so the UI is correct even for older HITL items in the queue.
  const radios: Array<{ value: ThresholdAction; label: string; detail: string }> = [
    {
      value: 'accept',
      label: 'Accept violations',
      detail:
        'Leave the data as-is. Violations stay recorded in the cross-field flags for review.',
    },
  ];
  if (!isNotNullWhen) {
    radios.push({
      value: 'clear_failing_cells',
      label: 'Clear failing cells',
      detail: `Both ${candidate.rule.col_a} and ${candidate.rule.col_b} will be set to null on the failing rows. Other cells in those rows are unchanged.`,
    });
  }
  radios.push({
    value: 'tag_for_review',
    label: 'Tag for review',
    detail:
      'Adds a "Raydn Flags for review" column to the cleansed CSV listing the affected column names per row. Cells themselves are unchanged.',
  });

  const dispatch = async (action: HitlActionRequest) => {
    setPending(action.type);
    setError(null);
    try {
      await onAction(hit.hit_id, action);
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const onConfirmClick = () => {
    if (!chosen) return;
    return dispatch({ type: chosen, reviewer_id: reviewerId });
  };

  return (
    <li
      data-rule-card="validator-threshold"
      data-resolution-type="cross_field_rule"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <CardHeader
        title={renderRuleDescription(candidate.rule)}
        tierLabel="threshold breach"
        subtitle={hit.issue}
      />
      <RuleSummary rule={candidate.rule} />
      {error && <p className="text-sm text-danger">{error}</p>}
      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              How should Raydn handle the violations?
            </legend>
            {radios.map((r) => (
              <label
                key={r.value}
                className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30"
              >
                <input
                  type="radio"
                  name={`threshold-${hit.hit_id}`}
                  value={r.value}
                  checked={chosen === r.value}
                  onChange={() => setChosen(r.value)}
                  className="mt-0.5"
                  data-testid={`threshold-radio-${r.value}`}
                />
                <span className="flex-1">
                  <span className="block font-medium text-sm">{r.label}</span>
                  <span className="block text-xs text-muted mt-0.5">
                    {r.detail}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirmClick}
              disabled={chosen === null || pending !== null}
              data-testid="threshold-confirm"
            >
              {pending !== null ? 'Confirming…' : 'Confirm'}
            </Button>
            {/*
              * v0.5 Stage E.1 — Revisit button reopens the underlying
              * rule decision. Writes a rule-scoped "revisited" audit
              * event (target_type="rule", event_type="revisited")
              * which invalidates the prior confirm/reject for
              * carry-forward; the pipeline re-kicks and CPA emits a
              * fresh confirmation HITL.
              */}
            <Button
              variant="ghost"
              onClick={() =>
                dispatch({ type: 'revisit_rule', reviewer_id: reviewerId })
              }
              disabled={pending !== null}
              data-testid="threshold-revisit-rule"
              title="Re-open the rule confirmation. Resets the prior decision so CPA proposes the rule again on the next run."
            >
              {pending === 'revisit_rule'
                ? 'Reopening rule…'
                : 'Revisit rule'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function CardHeader({
  title,
  tierLabel,
  subtitle,
}: {
  title: string;
  tierLabel: string;
  subtitle?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="m-0 text-sm font-semibold flex-1">{title}</h3>
        <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
          {tierLabel}
        </span>
      </div>
      {subtitle && <p className="text-xs text-muted m-0">{subtitle}</p>}
    </div>
  );
}

function RuleSummary({ rule }: { rule: CrossFieldRule }) {
  // v0.5 Stage E.2 — sum rules render col_a + glyph + parts list
  // (instead of a single col_b). The parts cell shows the joined
  // names, capped to a sensible visual width via wrap.
  if (isSumOperator(rule.operator)) {
    const parts = rule.sum_columns ?? [];
    const partsDisplay = parts.length > 0 ? parts.join(' + ') : '—';
    return (
      <div className="grid grid-cols-3 gap-2 text-xs font-mono items-center bg-surface-alt/30 rounded px-3 py-2">
        <div className="truncate" title={rule.col_a}>
          <span className="text-muted block text-[10px] uppercase">col_a</span>
          <span>{rule.col_a}</span>
        </div>
        <div className="text-center text-base font-semibold">
          {operatorGlyph(rule.operator)}
        </div>
        <div
          className="text-right break-words"
          title={partsDisplay}
          data-testid="rule-summary-parts"
        >
          <span className="text-muted block text-[10px] uppercase">
            sum of parts
          </span>
          <span>{partsDisplay}</span>
        </div>
        <div className="col-span-3 text-[11px] text-muted pt-1 border-t border-border/50">
          tolerance: ±{(rule.tolerance ?? DEFAULT_SUM_TOLERANCE).toFixed(2)}
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2 text-xs font-mono items-center bg-surface-alt/30 rounded px-3 py-2">
      <div className="truncate" title={rule.col_a}>
        <span className="text-muted block text-[10px] uppercase">col_a</span>
        <span>{rule.col_a}</span>
      </div>
      <div className="text-center text-base font-semibold">
        {operatorGlyph(rule.operator)}
      </div>
      <div className="truncate text-right" title={rule.col_b}>
        <span className="text-muted block text-[10px] uppercase">col_b</span>
        <span>{rule.col_b ?? '—'}</span>
      </div>
      {rule.condition && (
        <div className="col-span-3 text-[11px] text-muted pt-1 border-t border-border/50">
          condition: {rule.condition.col} = {String(rule.condition.value)}
        </div>
      )}
    </div>
  );
}

/**
 * v0.5 Stage E.2 — chip-style parts-list editor + tolerance input
 * for summation rules.
 *
 * Reviewer can:
 * - Remove an existing part by clicking the × on its chip.
 * - Add a new part by typing the column name + Enter (or clicking Add).
 * - Adjust the tolerance via the numeric input (defaulting to 0.01).
 *
 * The component is purely presentational — state lives in the parent
 * ``CpaConfirmationCard`` so it can compare against the original
 * candidate to decide between ``confirm_rule`` and ``modify_rule``
 * dispatch.
 */
interface SumRuleEditorProps {
  sumColumns: string[];
  tolerance: number;
  onSumColumnsChange: (cols: string[]) => void;
  onToleranceChange: (t: number) => void;
  disabled?: boolean;
}

function SumRuleEditor({
  sumColumns,
  tolerance,
  onSumColumnsChange,
  onToleranceChange,
  disabled = false,
}: SumRuleEditorProps) {
  const [draft, setDraft] = useState('');

  const onAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (sumColumns.includes(trimmed)) {
      // Duplicate — clear the input but don't error; the reviewer
      // can see the chip is already there.
      setDraft('');
      return;
    }
    onSumColumnsChange([...sumColumns, trimmed]);
    setDraft('');
  };

  const onRemove = (col: string) => {
    onSumColumnsChange(sumColumns.filter((c) => c !== col));
  };

  return (
    <div
      className="space-y-2 rounded border border-border bg-surface-alt/20 px-3 py-2"
      data-testid="sum-rule-editor"
    >
      <div className="space-y-1">
        <span className="text-xs uppercase tracking-wide text-muted">
          Parts
        </span>
        <ul
          className="flex flex-wrap gap-1"
          data-testid="sum-rule-parts-chips"
        >
          {sumColumns.length === 0 && (
            <li className="text-xs italic text-muted">
              No parts — add at least one column to confirm.
            </li>
          )}
          {sumColumns.map((col) => (
            <li key={col} className="inline-flex">
              <span
                className="inline-flex items-center gap-1 rounded-full border border-border bg-paper px-2 py-0.5 text-xs font-mono"
                data-testid={`sum-rule-part-${col}`}
              >
                <span>{col}</span>
                <button
                  type="button"
                  onClick={() => onRemove(col)}
                  disabled={disabled}
                  className="text-muted hover:text-danger focus:outline-none focus:ring-1 focus:ring-brand rounded-sm"
                  aria-label={`Remove ${col}`}
                  data-testid={`sum-rule-remove-${col}`}
                >
                  ×
                </button>
              </span>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdd();
              }
            }}
            disabled={disabled}
            placeholder="column name"
            className="flex-1 rounded border border-border bg-paper px-2 py-1 text-xs font-mono"
            data-testid="sum-rule-add-input"
          />
          <Button
            variant="secondary"
            onClick={onAdd}
            disabled={disabled || draft.trim().length === 0}
            data-testid="sum-rule-add-button"
          >
            Add
          </Button>
        </div>
      </div>
      <label className="block space-y-1">
        <span className="text-xs uppercase tracking-wide text-muted">
          Tolerance
        </span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={tolerance}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next) && next >= 0) {
              onToleranceChange(next);
            }
          }}
          disabled={disabled}
          className="w-32 rounded border border-border bg-paper px-2 py-1 text-xs font-mono"
          data-testid="sum-rule-tolerance-input"
        />
      </label>
    </div>
  );
}

function OperatorPicker({
  value,
  onChange,
}: {
  value: RuleOperator;
  onChange: (op: RuleOperator) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wide text-muted">
        Pick operator
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as RuleOperator)}
        className="block w-full rounded border border-border bg-paper px-2 py-1 text-sm"
        data-testid="rule-operator-picker"
      >
        {OPERATOR_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function DegradedRuleCard({ hit }: { hit: HitlItem }) {
  return (
    <li
      data-rule-card="degraded"
      className="rounded-md border bg-paper border-border p-4"
    >
      <h3 className="m-0 text-sm font-semibold text-warn">
        Cross-field rule item
      </h3>
      <p className="text-sm text-muted mt-1">
        This rule HITL is missing its candidate payload — the card cannot
        render. Issue: {hit.issue || '(no description)'}.
      </p>
    </li>
  );
}
